import { Banknote } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/States'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtMoney } from '../shared/format'
import { KMHSection } from './components/credit/KMHSection'
import { LoansSection } from './components/credit/LoansSection'
import { ReminderSettingsButton } from './components/credit/ReminderSettings'
import { KMH_ACCRUE, KMH_READ } from './kmh'
import { useCashAdvanceLimits } from './loans'
import {
  FINANCE_CASH_ADVANCE_CREATE,
  FINANCE_CASH_ADVANCE_DELETE,
  FINANCE_CASH_ADVANCE_EDIT,
  FINANCE_CASH_ADVANCE_PAY,
  FINANCE_CASH_ADVANCE_UNPAY,
} from './permissions'

/**
 * Şirket nakit avansları: banka hesaplarının nakit avans limiti (kullanılan / kalan) ve taksitli / taksitsiz kullanımlar.
 * Her işlem (kullanım, ödeme, geri alma, düzeltme, silme) ayrı yetkiye bağlı.
 */
export function FinanceCashAdvancesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const limits = useCashAdvanceLimits()
  const perms = {
    create: can(FINANCE_CASH_ADVANCE_CREATE),
    pay: can(FINANCE_CASH_ADVANCE_PAY),
    unpay: can(FINANCE_CASH_ADVANCE_UNPAY),
    edit: can(FINANCE_CASH_ADVANCE_EDIT),
    delete: can(FINANCE_CASH_ADVANCE_DELETE),
  }
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.cashAdvances.title')}
        description={t('finance.cashAdvances.subtitle')}
        actions={<ReminderSettingsButton scope="company" size="default" />}
      />
      <div className="space-y-4">
        <Section title={t('finance.cashAdvances.limits')} icon={Banknote} bodyClassName="p-0">
          {limits.isLoading ? (
            <Skeleton className="m-4 h-16" />
          ) : !limits.data?.length ? (
            <EmptyState icon={Banknote} title={t('finance.cashAdvances.noLimitAccounts')} />
          ) : (
            <ul className="divide-y">
              {limits.data.map((l) => {
                // Limit nakit avans ve KMH (eksi bakiye) için ortak.
                const pct = (x: string) => (Number(l.limit) > 0 ? Math.min(100, (Number(x) / Number(l.limit)) * 100) : 0)
                const used = pct(l.used)
                const kmh = Math.min(100 - used, pct(l.kmhUsed))
                return (
                  <li key={l.accountId} className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 text-sm">
                    <div className="min-w-0 flex-1 basis-48">
                      <p className="font-medium">{l.name}</p>
                      {l.bankName ? <p className="text-muted-foreground text-xs">{l.bankName}</p> : null}
                    </div>
                    <div className="w-full max-w-xs flex-1 basis-48 space-y-1">
                      <div
                        className="bg-muted h-2 overflow-hidden rounded-full"
                        role="progressbar"
                        aria-valuenow={Math.round(used + kmh)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div className="flex h-full">
                          <div className="bg-primary h-full" style={{ width: `${used}%` }} />
                          <div className="bg-destructive/70 h-full" style={{ width: `${kmh}%` }} />
                        </div>
                      </div>
                      <p className="text-muted-foreground text-xs tabular-nums">
                        {t('finance.cashAdvances.usage', { used: fmtMoney(l.used), limit: fmtMoney(l.limit) })}
                        {Number(l.kmhUsed) > 0 ? ` · ${t('finance.cashAdvances.kmhUsage', { amount: fmtMoney(l.kmhUsed) })}` : ''}
                      </p>
                    </div>
                    <p className="text-sm font-semibold tabular-nums">
                      {t('finance.cashAdvances.available', { amount: fmtMoney(l.available) })}
                    </p>
                  </li>
                )
              })}
            </ul>
          )}
        </Section>
        {can(KMH_READ) ? <KMHSection canAccrue={can(KMH_ACCRUE)} /> : null}
        <LoansSection kind="cash_advance" scope="company" perms={perms} />
      </div>
    </>
  )
}
